import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, Fragment } from "react";
import { Form, Badge, Button } from "react-bootstrap";

const integerEventOptions = {
  pass: {
    packing: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    direction: [-1, 0, 1],
    pass_distance: ["short", "middle", "long"],
  },
  dribble: {
    packing: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  },
};

const SubEventsCell = ({ getValue, row, column, table, fieldName }) => {
  const [data, setData] = useState();
  const [booleans, setBooleans] = useState([]);
  const [integers, setIntegers] = useState([]);
  const [subEvents, setSubEvents] = useState();
  const [selectedEvent, setSelectedEvent] = useState("disabled");
  const [visibleSubEvents, setVisibleSubEvents] = useState([]);

  const { event_type } = row.original;
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const editing = Number(tableMeta?.editingId) === Number(row.original.id);

  useEffect(() => {
    if (event_type && (booleans?.length > 0 || integers?.length > 0)) {
      setBooleans([]);
      setIntegers([]);
    }
  }, [event_type]);

  useEffect(() => {
    if (columnMeta?.subEvents?.types) {
      setSubEvents(columnMeta?.subEvents?.types[event_type]);
    }
  }, [columnMeta, event_type]);

  useEffect(() => {
    setData(getValue());
  }, [getValue]);

  useEffect(() => {
    let bools = [],
      ints = [];
    for (const eventName in data) {
      if (typeof data[eventName] === "boolean") bools = [...bools, eventName];
      else if (typeof data[eventName] === "number")
        ints = [...ints, [eventName, data[eventName]]];
    }
    setBooleans(bools);
    setIntegers(ints);
  }, [data]);

  const updateData = (booleans, integers) => {
    let newValue = {};
    for (const eventName of booleans) {
      newValue = {
        ...newValue,
        [eventName]: true,
      };
    }
    for (const event of integers) {
      newValue = {
        ...newValue,
        [event[0]]: event[1],
      };
    }
    tableMeta?.updateData(
      row.index,
      column.id,
      row.original.id,
      fieldName,
      newValue
    );
  };

  useEffect(() => {
    if (!subEvents || !Array.isArray(subEvents)) return;
    setVisibleSubEvents(
      subEvents.filter(
        (event) =>
          !booleans.includes(event) &&
          !integers.map(([name]) => name).includes(event)
      )
    );
  }, [subEvents, booleans, integers]);

  useEffect(() => {
    if (!editing) {
      setSelectedEvent("disabled");
      tableMeta?.closeSubEventsList?.();
    } else if (editing && visibleSubEvents.length > 0) {
      tableMeta?.showSubEventsList(visibleSubEvents, addSubEvent, event_type);
    }
  }, [editing, visibleSubEvents]);

  const handleDelete = (eventName) => {
    if (editing) {
      const newBooleans = booleans.filter((boolean) => boolean !== eventName);
      const newIntegers = integers.filter(([name]) => name !== eventName);
      updateData(newBooleans, newIntegers);
      setBooleans(newBooleans);
      setIntegers(newIntegers);
    }
  };

  const addSubEvent = (eventName) => {
    if (integerEventOptions?.[event_type]?.[eventName]) {
      const newIntegers = [
        ...integers,
        [eventName, integerEventOptions?.[event_type]?.[eventName][0]],
      ];
      updateData(booleans, newIntegers);
      setIntegers(newIntegers);
    } else {
      updateData([...booleans, eventName], integers);
      setBooleans([...booleans, eventName]);
    }
    setSelectedEvent("disabled");
  };

  const changeIntegerEvent = (eventName, newValue) => {
    const integer = integers.find(([name, val]) => name === eventName);
    integer[1] = Number(newValue);
    updateData(booleans, integers);
    setIntegers([...integers]);
  };

  const removeIntegerEvent = (eventName) => {
    const newIntegers = integers.filter(([name]) => name !== eventName);
    updateData(booleans, newIntegers);
    setIntegers(newIntegers);
  };

  return (
    <>
      <div
        className="grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridGap: "5px",
        }}
      >
        {booleans.map((eventName, index) => (
          <Badge
            key={index}
            className="d-flex align-items-center justify-content-center gap-2 sub-boolean-event"
            bg="success"
            style={editing ? { cursor: "pointer" } : {}}
            onClick={() => handleDelete(eventName)}
          >
            {eventName}{" "}
            {editing ? (
              <FontAwesomeIcon icon={["fas", "xmark"]} size="sm" />
            ) : null}
          </Badge>
        ))}
        {integers.map(([eventName, val], index) => (
          <Fragment key={index}>
            {integerEventOptions[event_type] ? (
              <>
                {!editing ? (
                  <Badge
                    className="d-flex align-items-center justify-content-center sub-boolean-event"
                    bg="success"
                  >
                    {eventName} {val}
                  </Badge>
                ) : (
                  <div className="d-flex gap-0">
                    <Form.Select
                      size="sm"
                      className="sub-integer-event-select"
                      defaultValue="disabled"
                      value={val}
                      onChange={(e) =>
                        changeIntegerEvent(eventName, e.target.value)
                      }
                    >
                      {integerEventOptions[event_type][eventName]?.map(
                        (option) => (
                          <option key={option} value={option}>
                            {eventName} {option}
                          </option>
                        )
                      )}
                    </Form.Select>
                    <span
                      className="sub-integer-event-delete"
                      onClick={() => removeIntegerEvent(eventName)}
                    >
                      <FontAwesomeIcon icon={["fas", "xmark"]} size="sm" />
                    </span>
                  </div>
                )}
              </>
            ) : null}
          </Fragment>
        ))}
        {editing && visibleSubEvents.length > 0 ? (
          <Form.Select
            size="sm"
            className="sub-event-select"
            defaultValue="disabled"
            value={selectedEvent}
            onChange={(e) => addSubEvent(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <option
              key="disabled"
              value="disabled"
              disabled
              style={{ display: "none" }}
            >
              + ADD
            </option>
            {visibleSubEvents.map((event) => (
              <option key={event} label={"_" + event} value={event}>
                {event}
              </option>
            ))}
          </Form.Select>
        ) : null}
      </div>
    </>
  );
};

export default SubEventsCell;
